import React, { useEffect, useRef } from "react";
import { Grid, Icon, IconButton, FormHelperText } from "@mui/material";
import MDBox from "components/MDBox";
import AutoComplete from "modules/AutoComplete/AutoComplete";
import PlantOptions from "modules/plants/PlantOptions";
import FormField from "components/FormField/FormField";
import CustomSelect from "components/Shared/CustomSelect/CustomSelect";
import { Controller, useFormContext } from "react-hook-form";
import { isNilOrEmpty } from "ramda-adjunct";
import { OrganizationProductType, ProductType } from "generated/graphql";
import { applicationRateMappings } from "modules/products/data/applicationRateMappings";
import useGetPlantLazy from "hooks/organization/plants/useGetPlantLazy";

interface PlantProductFieldProps {
  index: number;
  inputKey: string;
  productType: OrganizationProductType;
  onRemove: () => void;
}

export default function PlantProductField({
  index,
  inputKey,
  productType,
  onRemove,
}: PlantProductFieldProps) {
  const {
    control,
    watch,
    register,
    formState: { errors },
  } = useFormContext();

  const [getPlant, { plant }] = useGetPlantLazy();

  const plantId = watch(`plantProducts.${inputKey}.${index}.plantId`);

  const previousPlantId = useRef<string | null>(null);

  const shouldShowSelectForApplicationRate =
    productType.type === ProductType.ASPHALT ||
    productType.type === ProductType.ASPHALT_CURBING ||
    productType.type === ProductType.EXCAVATE_OUT;

  const applicationRateOptions = shouldShowSelectForApplicationRate
    ? applicationRateMappings[productType.type as ProductType] || []
    : [];

  useEffect(() => {
    if (plantId && !isNilOrEmpty(plantId) && plantId !== previousPlantId.current) {
      getPlant(plantId);
      previousPlantId.current = plantId;
    }
  }, [plantId]);

  return (
    <PlantOptions selectedPlant={plant}>
      {({ plantOptions, plantProductOptions, loading, search }) => (
        <MDBox key={`plantProducts.${inputKey}.${index}`} mb={2}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={5}>
              <Controller
                name={`plantProducts.${inputKey}.${index}.plantId` as const}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AutoComplete
                    options={plantOptions}
                    field={field}
                    loading={loading}
                    error={(error?.message ?? "") as string}
                    label="Plant"
                    search={search}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name={`plantProducts.${inputKey}.${index}.id` as const}
                control={control}
                render={({ field, fieldState: { error } }) => {
                  const selectedPlantId = watch(`plantProducts.${inputKey}.${index}.plantId`);
                  return (
                    <AutoComplete
                      disabled={isNilOrEmpty(selectedPlantId)}
                      options={plantProductOptions[selectedPlantId] ?? []}
                      field={field}
                      loading={loading}
                      error={(error?.message ?? "") as string}
                      label="Plant Product"
                      search={search}
                    />
                  );
                }}
              />
            </Grid>
            <Grid item xs={12} md={2.5}>
              {shouldShowSelectForApplicationRate ? (
                <CustomSelect
                  label="Depth"
                  control={control}
                  fullWidth
                  selectVariant="standard"
                  data={applicationRateOptions}
                  name={`plantProducts.${inputKey}.${index}.applicationRate`}
                />
              ) : (
                <FormField
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  label="Depth"
                  placeholder="Application Rate"
                  error={errors.plantProducts?.[inputKey]?.[index]?.applicationRate}
                  {...register(`plantProducts.${inputKey}.${index}.applicationRate`, {
                    valueAsNumber: true,
                  })}
                />
              )}
            </Grid>
            <Grid item xs={12} md={0.5}>
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                height="100%"
                pl={0}
              >
                <IconButton
                  //@ts-ignore
                  color="negative"
                  onClick={onRemove}
                >
                  <Icon fontSize="large" color="inherit">
                    delete
                  </Icon>
                </IconButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      )}
    </PlantOptions>
  );
}
